// Ported from vlookup-dramatic/components/ui/accordion.tsx without Base UI.
// <Accordion openMultiple={false} defaultValue={["a"]}>
//   <AccordionItem value="a"><AccordionTrigger>…</AccordionTrigger><AccordionContent>…</AccordionContent></AccordionItem>
// </Accordion>
import React, { createContext, useContext, useState } from "react";
import { cn } from "../../lib/utils";
import { ChevronDownIcon, ChevronUpIcon } from "../../lib/icons";

const Ctx = createContext({ open: [], toggle: () => {} });
const ItemCtx = createContext({ value: "", isOpen: false });

// `multiple` is Base UI's name for the same prop — both are accepted.
export function Accordion({ className, openMultiple, multiple, defaultValue, value, onValueChange, children, ...props }) {
  const allowMultiple = openMultiple !== undefined ? openMultiple : multiple !== undefined ? multiple : true;
  const [internal, setInternal] = useState(defaultValue || []);
  const open = value || internal;
  const toggle = (v) => {
    const next = open.includes(v) ? open.filter((x) => x !== v) : allowMultiple ? [...open, v] : [v];
    if (!value) setInternal(next);
    if (onValueChange) onValueChange(next);
  };
  return (
    <Ctx.Provider value={{ open, toggle }}>
      <div data-slot="accordion" className={cn("flex w-full flex-col", className)} {...props}>
        {children}
      </div>
    </Ctx.Provider>
  );
}

export function AccordionItem({ className, value, children, ...props }) {
  const { open } = useContext(Ctx);
  const isOpen = open.includes(value);
  return (
    <ItemCtx.Provider value={{ value, isOpen }}>
      <div
        data-slot="accordion-item"
        data-state={isOpen ? "open" : "closed"}
        className={cn("not-last:border-b", className)}
        {...props}
      >
        {children}
      </div>
    </ItemCtx.Provider>
  );
}

export function AccordionTrigger({ className, children, ...props }) {
  const { toggle } = useContext(Ctx);
  const { value, isOpen } = useContext(ItemCtx);
  return (
    <div className="flex">
      <button
        type="button"
        data-slot="accordion-trigger"
        aria-expanded={isOpen}
        onClick={() => toggle(value)}
        className={cn(
          "group/accordion-trigger relative flex flex-1 items-start justify-between rounded-lg border border-transparent py-2.5 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:after:border-ring aria-disabled:pointer-events-none aria-disabled:opacity-50 **:data-[slot=accordion-trigger-icon]:ml-auto **:data-[slot=accordion-trigger-icon]:size-4 **:data-[slot=accordion-trigger-icon]:text-muted-foreground",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon data-slot="accordion-trigger-icon" className="pointer-events-none shrink-0 group-aria-expanded/accordion-trigger:hidden" />
        <ChevronUpIcon data-slot="accordion-trigger-icon" className="pointer-events-none hidden shrink-0 group-aria-expanded/accordion-trigger:inline" />
      </button>
    </div>
  );
}

export function AccordionContent({ className, children, ...props }) {
  const { isOpen } = useContext(ItemCtx);
  if (!isOpen) return null;
  return (
    <div data-slot="accordion-content" data-state="open" className="overflow-hidden text-sm" {...props}>
      <div className={cn("pt-0 pb-2.5 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4", className)}>
        {children}
      </div>
    </div>
  );
}
